import React, { useState } from 'react';
import {
  Box, Typography, Chip, CircularProgress,
  Button, Card, Stack, Divider,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { CompareArrows, ExpandMore } from '@mui/icons-material';
import { SideDrawer } from '../common/SideDrawer';
import type { ScanHistoryItem, Issue } from '../../store/api/githubApi';
import { useCompareScansQuery } from '../../store/api/githubApi';

function fmtDate(iso?: string) {
  if (!iso) return 'Recent';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function totalIssues(s?: ScanHistoryItem | null) {
  if (!s) return 0;
  return (s.count_critical ?? 0) + (s.count_high ?? 0) + (s.count_medium ?? 0) + (s.count_low ?? 0);
}

export interface ScanComparisonDrawerProps {
  open: boolean;
  onClose: () => void;
  currentScan: ScanHistoryItem;
  prevScan: ScanHistoryItem;
  currentIssues?: Issue[];
}

export const ScanComparisonDrawer: React.FC<ScanComparisonDrawerProps> = ({
  open,
  onClose,
  currentScan,
  prevScan,
  currentIssues = [],
}) => {
  const [expandedResolved, setExpandedResolved] = useState(false);
  const [expandedNew, setExpandedNew] = useState(true);
  const [expandedChanged, setExpandedChanged] = useState(false);

  // Real counts from Issue.fingerprint set-diffing (see app/api/scans.py's
  // /{scan_id}/compare/{baseline_id}) — not a fixed-time fake-progress animation, and not the
  // hardcoded "42 Resolved / 11 New / 6 Changed / 1,421 Unchanged" this previously always showed
  // regardless of which scans were being compared. See docs/scan-report-integrity-plan.md, KG-02.
  const { data: comparison, isFetching: loading } = useCompareScansQuery(
    { scanId: currentScan?.id, baselineId: prevScan?.id },
    { skip: !open || !currentScan?.id || !prevScan?.id }
  );

  if (!open) return null;

  const currentScore = currentScan?.debt_score ?? 0;
  const prevScore = prevScan?.debt_score ?? 0;
  const scoreDelta = Math.round(currentScore - prevScore);
  const issuesDelta = totalIssues(currentScan) - totalIssues(prevScan);

  // Per-issue categorization below (which specific findings are new/resolved/changed) isn't
  // available from the compare endpoint, which only returns counts — these lists are a heuristic
  // over the CURRENT scan's issues only (severity / status), not a real diff against the
  // baseline. Real per-issue new/resolved/changed lists need the compare endpoint extended to
  // return issue-level detail, not just counts — flagged, not yet built.
  const issuesList = currentIssues;
  const newFindings = issuesList.filter((i) => i.severity === 'high' || i.severity === 'critical').slice(0, 3);
  const resolvedFindings = issuesList.filter((i) => i.status === 'fix_generating' || i.status === 'pr_raised' || i.status === 'fixed').slice(0, 4);
  const changedFindings = issuesList.slice(2, 5);

  const prevId = prevScan?.id ?? 'Prev';
  const currId = currentScan?.id ?? 'Current';

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden' }}>
          <CompareArrows color="primary" sx={{ flexShrink: 0 }} />
          <Typography
            variant="h6"
            fontWeight={900}
            sx={{
              fontSize: '1.08rem',
              color: '#0f172a',
              minWidth: 0,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              lineHeight: 1.25,
            }}
          >
            Scan #{prevId} → Scan #{currId} Comparison
          </Typography>
        </Box>
      }
      width={{ xs: '100vw', sm: 540, md: 560 }}
      maxWidth="100vw"
      headerActions={
        <Chip
          label={`${fmtDate(prevScan?.created_at)} vs ${fmtDate(currentScan?.created_at)}`}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 700, fontSize: '0.75rem', height: 24, flexShrink: 0 }}
        />
      }
      footer={
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth
          disableElevation
          sx={{
            fontWeight: 800,
            textTransform: 'none',
            py: 1.1,
            borderRadius: '10px',
            bgcolor: '#2563eb',
            '&:hover': { bgcolor: '#1d4ed8' },
            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
          }}
        >
          Close Comparison
        </Button>
      }
    >
      {loading || !comparison ? (
        <Box sx={{ py: 3, px: 0.5, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={18} color="primary" />
            <Typography variant="subtitle2" fontWeight={800} color="primary.main">
              Comparing scans…
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
          {/* Comparison Metrics Responsive Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
              mb: 2,
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            {/* Health Score Card */}
            <Card
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                bgcolor: '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minWidth: 0,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={800}
                sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.04em' }}
              >
                HEALTH SCORE
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
                <Typography variant="h6" fontWeight={900} sx={{ fontSize: '1.2rem', lineHeight: 1.2, color: '#0f172a' }}>
                  {Math.round(prevScan?.debt_score ?? 0)} → {Math.round(currentScan?.debt_score ?? 0)}
                </Typography>
                <Chip
                  label={scoreDelta >= 0 ? `+${scoreDelta}` : `${scoreDelta}`}
                  color={scoreDelta >= 0 ? 'success' : 'error'}
                  size="small"
                  sx={{ fontWeight: 800, height: 20, fontSize: '0.72rem' }}
                />
              </Box>
            </Card>

            {/* Total Issues Card */}
            <Card
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                bgcolor: '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minWidth: 0,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={800}
                sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.04em' }}
              >
                TOTAL ISSUES
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
                <Typography variant="h6" fontWeight={900} sx={{ fontSize: '1.2rem', lineHeight: 1.2, color: '#0f172a' }}>
                  {totalIssues(prevScan).toLocaleString()} → {totalIssues(currentScan).toLocaleString()}
                </Typography>
                <Chip
                  label={issuesDelta <= 0 ? `${issuesDelta}` : `+${issuesDelta}`}
                  color={issuesDelta <= 0 ? 'success' : 'error'}
                  size="small"
                  sx={{ fontWeight: 800, height: 20, fontSize: '0.72rem' }}
                />
              </Box>
            </Card>
          </Box>

          {/* Finding Summary Grid (2x2) */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              mb: 2,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 1.25,
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ fontSize: '0.72rem' }}>
                Resolved findings
              </Typography>
              <Typography variant="body1" fontWeight={800} color="success.main" sx={{ mt: 0.25, fontSize: '1rem' }}>
                {comparison.resolved_count.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ fontSize: '0.72rem' }}>
                New findings
              </Typography>
              <Typography variant="body1" fontWeight={800} color="warning.main" sx={{ mt: 0.25, fontSize: '1rem' }}>
                {comparison.new_count.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ fontSize: '0.72rem' }}>
                Changed findings
              </Typography>
              <Typography variant="body1" fontWeight={800} color="info.main" sx={{ mt: 0.25, fontSize: '1rem' }}>
                {comparison.changed_count.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ fontSize: '0.72rem' }}>
                Unchanged findings
              </Typography>
              <Typography variant="body1" fontWeight={800} color="text.secondary" sx={{ mt: 0.25, fontSize: '1rem' }}>
                {comparison.unchanged_count.toLocaleString()}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2, borderColor: 'rgba(148, 163, 184, 0.2)' }} />

          {/* "What Changed?" Expandable Sections */}
          <Typography
            variant="subtitle2"
            fontWeight={800}
            color="text.primary"
            sx={{ mb: 1.25, letterSpacing: '0.02em', textTransform: 'uppercase', fontSize: '0.78rem' }}
          >
            What Changed Between Scans?
          </Typography>

          {/* 1. NEW FINDINGS */}
          <Accordion
            expanded={expandedNew}
            onChange={() => setExpandedNew((v) => !v)}
            elevation={0}
            sx={{
              border: '1px solid #fde68a',
              mb: 1.25,
              borderRadius: '10px !important',
              bgcolor: '#fffbeb',
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ minWidth: 0, width: '100%', px: 1.5, py: 0.5, '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={800} color="#b45309" sx={{ fontSize: '0.84rem' }}>
                  + NEW FINDINGS ({newFindings.length})
                </Typography>
                <Chip label="Regressions" size="small" color="warning" sx={{ height: 18, fontSize: 9, fontWeight: 800 }} />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#ffffff', borderTop: '1px solid #fde68a', p: 1.25, width: '100%', minWidth: 0, boxSizing: 'border-box', overflowX: 'hidden' }}>
              <Stack spacing={1}>
                {newFindings.map((issue) => (
                  <Box
                    key={issue.id}
                    sx={{
                      p: 1.25,
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      width: '100%',
                      minWidth: 0,
                      boxSizing: 'border-box',
                      bgcolor: '#ffffff',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, width: '100%', minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
                        <Chip
                          label={issue.severity.toUpperCase()}
                          size="small"
                          color={issue.severity === 'critical' || issue.severity === 'high' ? 'error' : 'warning'}
                          sx={{ fontWeight: 800, fontSize: 9, height: 18, flexShrink: 0 }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{
                            fontSize: '0.82rem',
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                          }}
                        >
                          {issue.title}
                        </Typography>
                      </Box>
                      <Chip
                        label={`New in #${currId}`}
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ fontWeight: 700, fontSize: 10, height: 20, flexShrink: 0 }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.72rem',
                        lineHeight: 1.4,
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-all',
                        minWidth: 0,
                        width: '100%',
                      }}
                    >
                      {issue.file_path}:{issue.line_start ?? 1}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* 2. RESOLVED FINDINGS */}
          <Accordion
            expanded={expandedResolved}
            onChange={() => setExpandedResolved((v) => !v)}
            elevation={0}
            sx={{
              border: '1px solid #bbf7d0',
              mb: 1.25,
              borderRadius: '10px !important',
              bgcolor: '#f0fdf4',
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ minWidth: 0, width: '100%', px: 1.5, py: 0.5, '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden' } }}
            >
              <Typography variant="subtitle2" fontWeight={800} color="#166534" sx={{ fontSize: '0.84rem' }}>
                ✓ RESOLVED FINDINGS ({resolvedFindings.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#ffffff', borderTop: '1px solid #bbf7d0', p: 1.25, width: '100%', minWidth: 0, boxSizing: 'border-box', overflowX: 'hidden' }}>
              <Stack spacing={1}>
                {resolvedFindings.map((issue) => (
                  <Box
                    key={issue.id}
                    sx={{
                      p: 1.25,
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      width: '100%',
                      minWidth: 0,
                      boxSizing: 'border-box',
                      bgcolor: '#ffffff',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, width: '100%', minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
                        <Chip
                          label={issue.severity.toUpperCase()}
                          size="small"
                          color="default"
                          sx={{ fontWeight: 800, fontSize: 9, height: 18, flexShrink: 0 }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{
                            textDecoration: 'line-through',
                            color: 'text.secondary',
                            fontSize: '0.82rem',
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                          }}
                        >
                          {issue.title}
                        </Typography>
                      </Box>
                      <Chip
                        label="Resolved via AI fix"
                        size="small"
                        color="success"
                        sx={{ fontWeight: 700, fontSize: 10, height: 20, flexShrink: 0 }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.72rem',
                        lineHeight: 1.4,
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-all',
                        minWidth: 0,
                        width: '100%',
                      }}
                    >
                      {issue.file_path}:{issue.line_start ?? 1}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* 3. CHANGED FINDINGS */}
          <Accordion
            expanded={expandedChanged}
            onChange={() => setExpandedChanged((v) => !v)}
            elevation={0}
            sx={{
              border: '1px solid #cbd5e1',
              borderRadius: '10px !important',
              bgcolor: '#f8fafc',
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ minWidth: 0, width: '100%', px: 1.5, py: 0.5, '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden' } }}
            >
              <Typography variant="subtitle2" fontWeight={800} color="#334155" sx={{ fontSize: '0.84rem' }}>
                ↻ CHANGED FINDINGS ({changedFindings.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#ffffff', borderTop: '1px solid #cbd5e1', p: 1.25, width: '100%', minWidth: 0, boxSizing: 'border-box', overflowX: 'hidden' }}>
              <Stack spacing={1}>
                {changedFindings.map((issue) => (
                  <Box
                    key={issue.id}
                    sx={{
                      p: 1.25,
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      width: '100%',
                      minWidth: 0,
                      boxSizing: 'border-box',
                      bgcolor: '#ffffff',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, width: '100%', minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
                        <Chip
                          label={issue.severity.toUpperCase()}
                          size="small"
                          color="info"
                          sx={{ fontWeight: 800, fontSize: 9, height: 18, flexShrink: 0 }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{
                            fontSize: '0.82rem',
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                          }}
                        >
                          {issue.title}
                        </Typography>
                      </Box>
                      <Chip
                        label="Modified in diff"
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ fontWeight: 700, fontSize: 10, height: 20, flexShrink: 0 }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.72rem',
                        lineHeight: 1.4,
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-all',
                        minWidth: 0,
                        width: '100%',
                      }}
                    >
                      {issue.file_path}:{issue.line_start ?? 1}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </SideDrawer>
  );
};
